import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { FlashcardProvider } from './contexts/FlashcardContext';
import { GamificationProvider } from './contexts/GamificationContext';
import { PeerLearningProvider } from './contexts/PeerLearningContext';
import { CareerDevelopmentProvider } from './contexts/CareerDevelopmentContext';
import { ExpertSupportProvider } from './contexts/ExpertSupportContext';
import { DigitalLibraryProvider } from './contexts/DigitalLibraryContext';
import { ThemeProvider } from './context/ThemeContext';
import { MessagingProvider } from './contexts/MessagingContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import PrivateRoute from './components/PrivateRoute';
import SEO from './components/SEO';
import ErrorBoundary from './components/ErrorBoundary';
import ThemeToggle from './components/ThemeToggle';
import './App.css';
import Admin from './components/Admin';
import AdminAuth from './components/AdminAuth';
import Home from './components/Home';
import Login from './components/Login';
import Register from './components/Register';
import Profile from './components/Profile';
import Materials from './components/Materials';
import PeerLearning from './components/peer-learning/PeerLearning';
import Career from './components/career/Career';
import ExamPreparation from './components/exam-preparation/ExamPreparation';
import ExpertSupport from './components/expert-support/ExpertSupport';
import JobBoard from './components/job-board/JobBoard';
import Messaging from './components/messaging/Messaging';
import DigitalLibrary from './components/digital-library/DigitalLibrary';
import DigitalLibraryAdmin from './components/admin/DigitalLibraryAdmin';
import BasicChat from './components/messaging/BasicChat';
import { NotificationProvider } from './context/NotificationContext';
import NotificationDropdown from './components/NotificationDropdown';

// Lazy load components
const QandA = lazy(() => import('./components/QandA'));
const Contact = lazy(() => import('./components/Contact'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const NotFound = lazy(() => import('./components/NotFound'));
const FlashcardMaker = lazy(() => import('./components/flashcards/FlashcardMaker'));
const Gamification = lazy(() => import('./components/Gamification'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="loading-fallback">
    <div className="loading-spinner"></div>
    <p>Loading content...</p>
  </div>
);

const App = () => {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <ThemeProvider>
          <AuthProvider>
            <NotificationProvider>
              <MessagingProvider>
                <PeerLearningProvider>
                  <GamificationProvider>
                    <FlashcardProvider>
                      <CareerDevelopmentProvider>
                        <ExpertSupportProvider>
                          <DigitalLibraryProvider>
                            <Router>
                              <SEO 
                                title="Student Helper - Your Academic Companion"
                                description="Access study materials, ask questions, and collaborate with peers. Student Helper provides a comprehensive platform for students to enhance their learning experience."
                                keywords="student helper, education platform, study materials, homework help, student collaboration"
                              />
                              <div className="app">
                                <header className="app-header">
                                  <div className="header-left">
                                    <Navbar />
                                  </div>
                                  <div className="header-right">
                                    <NotificationDropdown />
                                    <ThemeToggle />
                                  </div>
                                </header>
                                <main className="main-content">
                                  <ErrorBoundary>
                                    <Suspense fallback={<LoadingFallback />}>
                                      <Routes>
                                        <Route path="/" element={<Home />} />
                                        <Route path="/qa" element={<QandA />} />
                                        <Route path="/materials" element={<Materials />} />
                                        <Route path="/career" element={<Career />} />
                                        <Route path="/profile" element={
                                          <PrivateRoute>
                                            <Profile />
                                          </PrivateRoute>
                                        } />
                                        <Route path="/contact" element={<Contact />} />
                                        <Route path="/login" element={<Login />} />
                                        <Route path="/register" element={<Register />} />
                                        <Route path="/forgot-password" element={<ForgotPassword />} />
                                        <Route path="/admin" element={
                                          <PrivateRoute>
                                            <Admin />
                                          </PrivateRoute>
                                        } />
                                        <Route path="/admin/digital-library" element={
                                          <PrivateRoute>
                                            <DigitalLibraryAdmin />
                                          </PrivateRoute>
                                        } />
                                        <Route path="/admin-auth" element={
                                          <PrivateRoute>
                                            <AdminAuth />
                                          </PrivateRoute>
                                        } />
                                        <Route path="/flashcards" element={
                                          <PrivateRoute>
                                            <FlashcardMaker />
                                          </PrivateRoute>
                                        } />
                                        <Route path="/achievements" element={
                                          <PrivateRoute>
                                            <Gamification />
                                          </PrivateRoute>
                                        } />
                                        <Route path="/peer-learning" element={
                                          <PrivateRoute>
                                            <PeerLearning />
                                          </PrivateRoute>
                                        } />
                                        <Route path="/exam-preparation" element={
                                          <PrivateRoute>
                                            <ExamPreparation />
                                          </PrivateRoute>
                                        } />
                                        <Route path="/expert-support" element={
                                          <PrivateRoute>
                                            <ExpertSupport />
                                          </PrivateRoute>
                                        } />
                                        <Route path="/job-board" element={
                                          <PrivateRoute>
                                            <JobBoard />
                                          </PrivateRoute>
                                        } />
                                        <Route path="/messaging" element={
                                          <PrivateRoute>
                                            <Messaging />
                                          </PrivateRoute>
                                        } />
                                        <Route path="/digital-library" element={<DigitalLibrary />} />
                                        <Route path="*" element={<NotFound />} />
                                      </Routes>
                                    </Suspense>
                                  </ErrorBoundary>
                                </main>
                                <Footer />
                                <BasicChat />
                              </div>
                            </Router>
                          </DigitalLibraryProvider>
                        </ExpertSupportProvider>
                      </CareerDevelopmentProvider>
                    </FlashcardProvider>
                  </GamificationProvider>
                </PeerLearningProvider>
              </MessagingProvider>
            </NotificationProvider>
          </AuthProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
};

export default App;
